import { ReactElement } from 'react';
import { MotionConfig } from 'framer-motion';

import WorkflowSubLayout from '@layouts/DashboardLayout/SubLayouts/Workflow';
import FormRequestDetails from '@lib/FormRequestDetails';
import CreditReportDetails from '@lib/CreditReportDetails';
import CustomTermsDetails from '@lib/CustomTermsDetails';
import WorkflowDetails from '@lib/WorkflowDetails';
import DocumentRequestUserDetail from '@lib/DocumentRequestUserDetailV3';
import InvoicePreview from '@lib/InvoicePreview/InvoicePreview';
import { DocumentAccessProvider } from '@providers/DocumentAccess';
import DocumentRequestUserFileDetail from '@lib/DocumentRequestUserFileDetail';
import AIFollowupHistory from '@lib/AIFollowupHistory';
import AIDocumentGenerationForm from '@components/AIDocumentGenerationForm';
import UserWorkflowDocuments from '@lib/UserWorkflowDocuments';
import DocumentPreview from '@lib/DocumentPreview/DocumentPreview';
import HelpCenterIframe from '@lib/HelpCenterIframe';
import UserWorkflowActivityThread from '@/lib/UserWorkflowActivityThread';
import QuestionnaireEditor from '@/components/Questionnaire/questionnaireEditor/QuestionnaireEditor';
import DataReferenceDetail from '@lib/DataReferenceDetail';
import PacerSubmissionPane from '@lib/PacerSubmissionPane';
import GovernmentFormPane from '@lib/EditGovernmentFormContent';
import PacerPreviewPane from '@/lib/PacerPreviewPane';
import BankStatementsOrganizer from '@/lib/BankStatementsOrganizer/index';
import InitiateCase from '@/lib/InitiateCase';

type Options = {
  type: string;
  id: string;
  key: string;
  creatorSlug: string | null;
  creatorId: string | null;
  creatorNoodlePhoneNumber: string | null;
  onCloseKey: (key: string) => void;
  onChangeTypeAndId: (type: string, id: string) => void;
  associatedUserWorkflowId?: string;
  customer?: {
    email: string | null;
    id: string;
    name: string | null;
  };
  owners?: Array<{
    personId: string;
    workflowRoles?: string[];
  }>;
};

const pickContent = ({
  type,
  id,
  key,
  creatorId,
  creatorSlug,
  creatorNoodlePhoneNumber,
  onCloseKey,
  onChangeTypeAndId,
  associatedUserWorkflowId,
  customer,
  owners,
}: Options): ReactElement | null => {

  if (type === 'initiateCase' && creatorId && creatorSlug) {
    return (
      <InitiateCase
        creatorId={creatorId}
        creatorSlug={creatorSlug}
        associatedUserWorkflowId={associatedUserWorkflowId}
        customer={customer}
        owners={owners}
      />
    );
  }

  if (type === 'dataReference') {
    return (
      <DataReferenceDetail dataReferenceId={id} />
    );
  }

  if (type === 'submissionAttempt') {
    return (
      <PacerSubmissionPane submissionAttemptId={id} />
    );
  }

  if (type === 'pacerPreview') {
    return (
      <DocumentAccessProvider>
        <PacerPreviewPane userWorkflowId={id} />
      </DocumentAccessProvider>
    );
  }

  if (type === 'formRequest' && creatorSlug) {
    return (
      <MotionConfig reducedMotion='always'>
        <FormRequestDetails creatorSlug={creatorSlug} formRequestId={id} onClose={() => onCloseKey(key)} />
      </MotionConfig>
    );
  }

  if (type === 'creditReport') {
    return (
      <CreditReportDetails creditReportId={id} />
    );
  }

  if (type === 'formRequestTemplate') {
    return (
      <QuestionnaireEditor slug={id} />
    );
  }

  if (type === 'customTerms') {
    return (
      <CustomTermsDetails customTermsId={id} />
    );
  }

  if (type === 'documentRequestUserFile') {
    return (
      <DocumentRequestUserFileDetail
        documentRequestUserFileId={id}
      />
    );
  }

  if (type === 'previewDocument') {
    return (
      <DocumentAccessProvider>
        <DocumentPreview documentId={id} />
      </DocumentAccessProvider>
    );
  }

  if (type === 'documentRequestUser' && creatorSlug) {
    return (
      <DocumentRequestUserDetail
        creatorSlug={creatorSlug}
        documentRequestUserId={id}
      />
    );
  }

  if (type === 'governmentForm') {
    return (
      <GovernmentFormPane id={id} />
    );
  }

  if (type === 'invoice' && creatorSlug) {
    return (
      <DocumentAccessProvider>
        <InvoicePreview creatorSlug={creatorSlug} invoiceId={id} replaceByTypeAndId={onChangeTypeAndId}/>
      </DocumentAccessProvider>
    );
  }

  if (type === 'followupHistory' && creatorId) {
    return (
      <AIFollowupHistory
        creatorId={creatorId}
        creatorNoodlePhoneNumber={creatorNoodlePhoneNumber}
        userWorkflowId={id}
      />
    );
  }

  if (type === 'aiFollowups' && creatorId) {
    return (
      <AIFollowupHistory
        creatorId={creatorId}
        creatorNoodlePhoneNumber={creatorNoodlePhoneNumber}
      />
    );
  }

  if (type === 'allCases') {
    return (
      <WorkflowSubLayout resourceType='activity' isTableViewOnly hideTabs/>
    );
  }

  if (type === 'helpCenter') {
    return (
      <HelpCenterIframe />
    );
  }

  if (type === 'userWorkflowDocument' && creatorSlug) {
    return (
      <AIDocumentGenerationForm
        creatorSlug={creatorSlug}
        userWorkflowDocumentId={id}
        onClose={() => onCloseKey(key)}
      />
    );
  }

  if (type === 'userWorkflowDocuments' && creatorId) {
    return (
      <DocumentAccessProvider>
        <UserWorkflowDocuments
          creatorId={creatorId}
          isDownloadEnabled
          userWorkflowId={id}
        />
      </DocumentAccessProvider>
    );
  }

  if (type === 'workflowTemplate') {
    return (
      <WorkflowDetails workflowSlug={id} />
    );
  }

  if (type === 'activityThread') {
    return (
      <UserWorkflowActivityThread userWorkflowId={id} />
    );
  }

  if (type === 'bankStatementOrganizer') {
    return (
      <BankStatementsOrganizer bankStatementOrganizerId={id} />
    );
  }

  return null;
};

export default pickContent;
